import {
  Inventory,
  Person,
  Settings,
  Analytics,
  LocalShipping,
  Campaign,
  GroupAdd,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAgenciaStore } from "../zustand/useAgenciaStore";
import { useAuthStore } from "../zustand/useAuthStore";
import { sidebarWidth, useSidebarStore } from "../zustand/useSidebarStore";
import { capitalizeString } from "../utils/capitalizeString";
import { UserRole } from "../interfaces/UserRole";

const ITEMS = [
  {
    title: "Paquetes",
    Icon: Inventory,
    path: "/",
  },
  {
    title: "Envíos",
    Icon: LocalShipping,
    path: "/shipments",
  },
  {
    title: "Estadísticas",
    Icon: Analytics,
    path: "/analytics",
  },
  {
    title: "Promociones",
    Icon: Campaign,
    path: "/promotions",
  },
  {
    title: "Usuarios",
    Icon: Person,
    path: "/users",
  },
  {
    title: "Referidos/Puntos",
    Icon: GroupAdd,
    path: "/referidos",
  },
  {
    title: "Configuración",
    Icon: Settings,
    path: "/settings",
  },
];

export default function AppBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { agencia } = useAgenciaStore();
  const { user } = useAuthStore();
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  const theme = useTheme();
  const drawerTextColor = theme.palette.custom?.drawerText || "#FFFFFF";
  const drawerWidth = sidebarWidth(collapsed);

  // Filtrar items del menú según el rol del usuario
  const filteredItems = ITEMS.filter((item) => {
    // Si es Digitador, solo mostrar Paquetes y Envíos
    if (user?.role === UserRole.Digitador) {
      return item.path === "/" || item.path === "/shipments";
    }
    // Si es Admin, mostrar todos los items
    if (user?.role === UserRole.Admin) {
      return true;
    }
    // Por defecto, mostrar todos (por si acaso)
    return true;
  });
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundColor: theme.palette.custom?.drawer || "#353a40",
          overflowX: "hidden",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      <List>
        <ListItem
          key={"logo"}
          sx={{
            padding: { xs: "8px 0", sm: "8px 0", md: "16px" },
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
            minHeight: 72,
          }}
        >
          {collapsed ? (
            <Tooltip title="Ampliar menú" placement="right">
              <IconButton
                onClick={toggle}
                aria-label="Ampliar menú"
                sx={{ color: drawerTextColor }}
              >
                <ChevronRight />
              </IconButton>
            </Tooltip>
          ) : (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Box sx={{ flexShrink: 0, display: "flex" }}>
                  <img
                    src={"/logo.png"}
                    alt={"Logo"}
                    style={{
                      width: "4rem",
                      display: "block",
                      filter: "drop-shadow(0 0 2px rgba(255, 255, 255, 0.5))",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: { xs: "none", sm: "none", md: "block" },
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: drawerTextColor, fontWeight: "bold", lineHeight: 1.1 }}
                  >
                    PackFlowing
                  </Typography>
                  <ListItemText
                    primary={capitalizeString(agencia?.suscripcion?.plan)}
                    sx={{
                      color: theme.palette.text.secondary,
                      margin: 0,
                      textAlign: "left",
                      "& .MuiListItemText-primary": {
                        fontSize: 12,
                        fontWeight: "bold",
                        lineHeight: 1.2,
                        color: theme.palette.text.secondary,
                      },
                    }}
                  />
                </Box>
              </Box>
              <Tooltip title="Colapsar menú" placement="right">
                <IconButton
                  onClick={toggle}
                  aria-label="Colapsar menú"
                  size="small"
                  sx={{ color: drawerTextColor, flexShrink: 0 }}
                >
                  <ChevronLeft />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </ListItem>
        <Divider sx={{ mx: 1, mb: 2, mt: 1 }} color={drawerTextColor} />
        {filteredItems.map(({ title, Icon, path }) => {
          const isSelected = location.pathname === path;
          const selectedBg = theme.palette.custom?.drawerSelected || "rgba(255, 255, 255, 0.1)";
          const hoverBg = theme.palette.custom?.drawerHover || "rgba(255, 255, 255, 0.05)";
          return (
            <ListItem key={title} disablePadding>
              <Tooltip title={collapsed ? title : ""} placement="right">
                <ListItemButton
                  onClick={() => navigate(path)}
                  selected={isSelected}
                  sx={{
                    padding: collapsed
                      ? "8px 0"
                      : { xs: "8px 0", sm: "8px 0", md: "8px 16px" },
                    justifyContent: collapsed
                      ? "center"
                      : {
                          xs: "center",
                          sm: "center",
                          md: "flex-start",
                        },
                    backgroundColor: isSelected ? selectedBg : "transparent",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? selectedBg.replace("0.1", "0.15")
                        : hoverBg,
                    },
                    "&.Mui-selected": {
                      backgroundColor: selectedBg,
                      borderLeft: `3px solid ${drawerTextColor}`,
                      "&:hover": {
                        backgroundColor: selectedBg.replace("0.1", "0.15"),
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{ justifyContent: "center", minWidth: collapsed ? 0 : undefined }}
                  >
                    <Icon sx={{ color: drawerTextColor, fontSize: "1.25rem" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={title}
                    sx={{
                      color: drawerTextColor,
                      display: collapsed
                        ? "none"
                        : { xs: "none", sm: "none", md: "block" },
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
}
